import { useState } from 'react';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { LoadingOverlay } from '@givebutter/primitives/loading-overlay';
import * as SingleSelect from '@givebutter/primitives/single-select';
import {
    faArchive,
    faArrowsRotate,
    faCirclePlus,
    faEnvelope,
    faEnvelopeOpen,
    faFile,
    faGift,
    faHandsHoldingHeart,
    faHandshake,
    faHeart,
    faHeartHalfStroke,
    faListCheck,
    faMessage,
    faMessageDots,
    faMessageSlash,
    faPaperPlaneTop,
    faPhone,
    faTicketSimple,
    faUser,
    faUserPlus,
    faVolumeOff,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchContactActivities } from 'api/contacts/activities';
import * as AddEvent from 'assets/js/pages/contacts/single/timeline/add-event';
import TimelineList from 'assets/js/pages/contacts/single/timeline/timeline-list';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { useIsPending } from 'customHooks/useIsPending';
import { transformInfiniteQueryOptions } from 'helpers/reactQuery';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';

const Timeline = () => {
    const { accountId } = useCurrentAccount();
    const { data: contact } = useCurrentContact();
    const contactId = contact.id;
    const [selectedFilter, setSelectedFilter] = useState<string>('none');
    const { transactionSoftCredits = false, pledgeActivities } = useFlags();

    const [isPending, params] = useIsPending(
        {
            type: selectedFilter,
        },
        500,
    );

    const { data, hasNextPage, fetchNextPage } = useSuspenseInfiniteQuery({
        initialPageParam: 1,
        queryKey: accountQueryKeys.contactActivitiesWithFilter(accountId, contactId, params.type),
        queryFn: ({ pageParam }) => {
            return fetchContactActivities(accountId, contactId, {
                options: {
                    page: pageParam,
                    limit: 20,
                },
                ...params,
            });
        },
        ...transformInfiniteQueryOptions(),
    });

    return (
        <LoadingOverlay isLoading={isPending}>
            <div className={'flex flex-col gap-8'}>
                <div className={'flex items-center justify-between'}>
                    <div className={'flex items-center gap-4'}>
                        <h2 className={'text-2xl font-medium'}>Activity Feed</h2>

                        <div className="w-72">
                            <SingleSelect.Root
                                value={selectedFilter}
                                onValueChange={setSelectedFilter}
                            >
                                <SingleSelect.Trigger aria-label="Activity feed filter">
                                    <SingleSelect.Value placeholder="Select a filter" />
                                </SingleSelect.Trigger>
                                <SingleSelect.Content>
                                    <SingleSelect.Item value="none">
                                        <SingleSelect.ItemText>Showing all</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="email">
                                        <FontAwesomeIcon
                                            icon={faPaperPlaneTop}
                                            rotation={270}
                                            className="size-4"
                                        />
                                        <SingleSelect.ItemText>Email</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="phone_call">
                                        <FontAwesomeIcon icon={faPhone} className="size-4" />
                                        <SingleSelect.ItemText>Phone Call</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="note">
                                        <FontAwesomeIcon icon={faFile} className="size-4" />
                                        <SingleSelect.ItemText>Note</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="sms">
                                        <FontAwesomeIcon icon={faMessageDots} className="size-4" />
                                        <SingleSelect.ItemText>Text message</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="volunteer_activity">
                                        <FontAwesomeIcon
                                            icon={faHandsHoldingHeart}
                                            className="size-4"
                                        />
                                        <SingleSelect.ItemText>
                                            Volunteer activity
                                        </SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="completed_task">
                                        <FontAwesomeIcon icon={faListCheck} className="size-4" />
                                        <SingleSelect.ItemText>
                                            Completed task
                                        </SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="meeting">
                                        <FontAwesomeIcon icon={faHandshake} className="size-4" />
                                        <SingleSelect.ItemText>Meeting</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="transaction.succeeded">
                                        <FontAwesomeIcon icon={faHeart} className="size-4" />
                                        <SingleSelect.ItemText>Transaction</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="transaction.recieved">
                                        <FontAwesomeIcon icon={faUser} className="size-4" />
                                        <SingleSelect.ItemText>
                                            Peer to Peer Credit
                                        </SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    {transactionSoftCredits && (
                                        <SingleSelect.Item value="soft_credited">
                                            <FontAwesomeIcon
                                                icon={faHeartHalfStroke}
                                                className="size-4"
                                            />
                                            <SingleSelect.ItemText>
                                                Soft Credit
                                            </SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                    )}
                                    <SingleSelect.Item value="recurring_plan.created">
                                        <FontAwesomeIcon icon={faArrowsRotate} className="size-4" />
                                        <SingleSelect.ItemText>
                                            Recurring Plan
                                        </SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="campaign.joined">
                                        <FontAwesomeIcon icon={faVolumeOff} className="size-4" />
                                        <SingleSelect.ItemText>
                                            Joined Campaign
                                        </SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="ticket.issued">
                                        <FontAwesomeIcon icon={faTicketSimple} className="size-4" />
                                        <SingleSelect.ItemText>Ticket</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="signup_form.submitted">
                                        <FontAwesomeIcon icon={faUserPlus} className="size-4" />
                                        <SingleSelect.ItemText>Signup Form</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="archived">
                                        <FontAwesomeIcon icon={faArchive} className="size-4" />
                                        <SingleSelect.ItemText>Archived</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="unarchived">
                                        <FontAwesomeIcon icon={faArchive} className="size-4" />
                                        <SingleSelect.ItemText>Unarchived</SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    {pledgeActivities && (
                                        <>
                                            <SingleSelect.Item value="pledge.created">
                                                <FontAwesomeIcon icon={faGift} className="size-4" />
                                                <SingleSelect.ItemText>
                                                    Pledge Created
                                                </SingleSelect.ItemText>
                                            </SingleSelect.Item>
                                            <SingleSelect.Item value="pledge.transaction.succeeded">
                                                <FontAwesomeIcon icon={faGift} className="size-4" />
                                                <SingleSelect.ItemText>
                                                    Pledge Installment Paid
                                                </SingleSelect.ItemText>
                                            </SingleSelect.Item>
                                            <SingleSelect.Item value="pledge.installment.off_schedule">
                                                <FontAwesomeIcon icon={faGift} className="size-4" />
                                                <SingleSelect.ItemText>
                                                    Pledge Off-schedule
                                                </SingleSelect.ItemText>
                                            </SingleSelect.Item>
                                        </>
                                    )}
                                    <SingleSelect.Item value="email.unsubscribed">
                                        <FontAwesomeIcon icon={faEnvelope} className="size-4" />
                                        <SingleSelect.ItemText>
                                            Email Unsubscribed
                                        </SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="email.subscribed">
                                        <FontAwesomeIcon icon={faEnvelopeOpen} className="size-4" />
                                        <SingleSelect.ItemText>
                                            Email Subscribed
                                        </SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="sms.unsubscribed">
                                        <FontAwesomeIcon icon={faMessageSlash} className="size-4" />
                                        <SingleSelect.ItemText>
                                            SMS Unsubscribed
                                        </SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                    <SingleSelect.Item value="sms.subscribed">
                                        <FontAwesomeIcon icon={faMessage} className="size-4" />
                                        <SingleSelect.ItemText>
                                            SMS Subscribed
                                        </SingleSelect.ItemText>
                                    </SingleSelect.Item>
                                </SingleSelect.Content>
                            </SingleSelect.Root>
                        </div>
                    </div>
                    {contact && (
                        <AddEvent.Root contact={contact}>
                            <AddEvent.Trigger asChild>
                                <Button
                                    variant="transparent"
                                    className="p-0"
                                    aria-label="Add event"
                                >
                                    <FontAwesomeIcon size={'2x'} icon={faCirclePlus} />
                                </Button>
                            </AddEvent.Trigger>
                        </AddEvent.Root>
                    )}
                </div>
                <div className="flex flex-col gap-4">
                    {contact && (
                        <TimelineList
                            activities={data?.pages ?? []}
                            contact={contact}
                            fetchTimeLine={() => {
                                queryClient.invalidateQueries({
                                    queryKey: accountQueryKeys.contactActivitiesWithFilter(
                                        accountId,
                                        contactId,
                                        selectedFilter,
                                    ),
                                });
                            }}
                            isLoading={isPending}
                        />
                    )}
                    {hasNextPage && (
                        <Button variant="solid" onClick={() => fetchNextPage()}>
                            Load More
                        </Button>
                    )}
                </div>
            </div>
        </LoadingOverlay>
    );
};

export default Timeline;
